import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import DropZone from "../components/DropZone";
import InsightBlocksEditor from "../components/InsightBlocksEditor";
import TranslationTabs from "../components/TranslationTabs";
import { Plus, Edit, Trash2, Save, ArrowLeft, ArrowDown, ArrowUp } from "lucide-react";

const EMPTY = { slug: "", title: "", topic: "", author: "", role: "", year: new Date().getFullYear(), readMin: 5, cover: "", cover_alt: "", excerpt: "", body: "", keyLearnings: [], content_blocks: [], translations: { en: {}, es: {} }, meta_title: "", meta_description: "", main_keyword: "", secondary_keywords: [], og_image: "", noindex: false };

const INSIGHT_TRANSLATABLE_FIELDS = [
  { key: "title", label: "Título" },
  { key: "excerpt", label: "Resumo", area: true },
  { key: "body", label: "Corpo do artigo (HTML)", area: true },
];

function resolveSrc(u) { if (!u) return ""; if (u.startsWith("http")) return u; if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`; return u; }

export function InsightsList() {
  const [items, setItems] = useState([]);
  // Sort ordering — controlled by KV `insights_config.sort` and mirrored to the
  // public /insights page. This block persists to the KV on toggle so the change
  // takes effect site-wide immediately (no separate config page needed).
  const [sort, setSort] = useState("newest"); // "newest" | "oldest"
  const [savingSort, setSavingSort] = useState(false);

  useEffect(() => {
    api.get("/content/insights").then(({ data }) => setItems(data));
    api.get("/content/kv/insights_config")
      .then(({ data }) => setSort((data?.sort || "newest").toLowerCase()))
      .catch(() => {});
  }, []);

  const setSortMode = async (mode) => {
    setSort(mode);
    setSavingSort(true);
    try {
      await api.put("/content/kv/insights_config", { value: { sort: mode } });
      toast.success(mode === "newest" ? "Ordem: mais novos primeiro." : "Ordem: mais antigos primeiro.");
    } catch {
      toast.error("Erro ao salvar ordem.");
    } finally {
      setSavingSort(false);
    }
  };

  // Order the visible list the SAME way as the public page — by year (fallback
  // to created_at), ascending/descending based on `sort`. Provides an accurate
  // preview of the audience experience directly inside the CMS.
  const sortedItems = React.useMemo(() => {
    const keyOf = (i) => `${String(i.year || "0000").padStart(4, "0")}_${i.created_at || i.updated_at || ""}`;
    return [...items].sort((a, b) => {
      const ka = keyOf(a); const kb = keyOf(b);
      return sort === "oldest" ? ka.localeCompare(kb) : kb.localeCompare(ka);
    });
  }, [items, sort]);

  const remove = async (id) => {
    if (!window.confirm("Excluir?")) return;
    await api.delete(`/admin/insights/${id}`);
    setItems(items.filter(i => i.id !== id));
    toast.success("Excluído.");
  };
  return (
    <div>
      <div className="flex justify-between items-end mb-6">
        <h1 className="font-display text-4xl">Insights</h1>
        <Link to="/admin/insights/new" className="bg-accent-yellow text-black text-sm font-semibold px-4 py-2 rounded-full" data-testid="insight-new"><Plus className="w-4 h-4 inline mr-1" /> Novo</Link>
      </div>

      {/* Sort toggle — controls both the CMS list and the public /insights page. */}
      <div className="border border-black/10 rounded-md p-3 mb-4 flex items-center gap-3 bg-neutral-50" data-testid="insights-sort-bar">
        <div className="text-xs uppercase tracking-widest font-semibold text-neutral-600 shrink-0">Ordenar por</div>
        <button
          type="button"
          onClick={() => setSortMode("newest")}
          disabled={savingSort}
          data-testid="insights-sort-newest"
          className={`text-xs px-3 py-1.5 rounded-full font-semibold transition flex items-center gap-1 ${sort === "newest" ? "bg-black text-white" : "bg-white text-neutral-600 border border-black/15 hover:border-black"}`}
        >
          <ArrowDown className="w-3 h-3" /> Mais novo → mais antigo
        </button>
        <button
          type="button"
          onClick={() => setSortMode("oldest")}
          disabled={savingSort}
          data-testid="insights-sort-oldest"
          className={`text-xs px-3 py-1.5 rounded-full font-semibold transition flex items-center gap-1 ${sort === "oldest" ? "bg-black text-white" : "bg-white text-neutral-600 border border-black/15 hover:border-black"}`}
        >
          <ArrowUp className="w-3 h-3" /> Mais antigo → mais novo
        </button>
        <div className="text-[10px] text-neutral-500 ml-2 hidden md:block">Salva automático · vale para o site público em <code>/insights</code></div>
      </div>

      <div className="space-y-2" data-testid="insights-list">
        {sortedItems.map((c) => (
          <div key={c.id} className="flex items-center gap-4 border border-black/10 p-3 rounded-md hover:bg-black/[0.02]">
            <div className="w-20 h-14 bg-neutral-200 overflow-hidden rounded">{c.cover && <img src={resolveSrc(c.cover)} alt="" className="w-full h-full object-cover" />}</div>
            <div className="flex-1 min-w-0"><div className="font-semibold truncate">{c.title}</div><div className="text-xs text-neutral-500">{c.author} · {c.topic} · {c.year}</div></div>
            <Link to={`/admin/insights/${c.id}`} className="text-xs border border-black/15 px-3 py-1.5 rounded"><Edit className="w-3 h-3 inline" /></Link>
            <button onClick={() => remove(c.id)} className="text-xs text-red-500 px-3 py-1.5"><Trash2 className="w-3 h-3 inline" /></button>
          </div>
        ))}
        {!sortedItems.length && <div className="text-sm text-neutral-500 text-center py-12">Nenhum insight ainda.</div>}
      </div>
    </div>
  );
}

export function InsightEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [item, setItem] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (isNew) return;
    api.get(`/content/insights/${id}`).then(({ data }) => setItem({ ...EMPTY, ...data, keyLearnings: data.keyLearnings || [] })).finally(() => setLoading(false));
  }, [id, isNew]);

  const upd = (k, v) => setItem((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = editorRef.current ? editorRef.current.getContent() : item.body;
      const data = { ...item, body, year: Number(item.year), readMin: Number(item.readMin) };
      data.slug = data.slug || data.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      if (typeof data.keyLearnings === "string") data.keyLearnings = data.keyLearnings.split("\n").map(s => s.trim()).filter(Boolean);
      if (typeof data.secondary_keywords === "string") data.secondary_keywords = data.secondary_keywords.split(",").map(s => s.trim()).filter(Boolean);
      if (isNew) {
        const { data: created } = await api.post("/admin/insights", data);
        toast.success("Insight criado.");
        navigate(`/admin/insights/${created.id}`);
      } else {
        await api.put(`/admin/insights/${id}`, data);
        toast.success("Salvo.");
      }
    } catch { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-neutral-500">Carregando…</div>;

  return (
    <div>
      <div className="flex justify-between items-end mb-6">
        <div><Link to="/admin/insights" className="text-xs text-neutral-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Voltar</Link><h1 className="font-display text-3xl mt-1">{isNew ? "Novo insight" : item.title}</h1></div>
        <div className="flex gap-2">
          {!isNew && item.slug && (<a href={`/insights/${item.slug}`} target="_blank" rel="noopener noreferrer" data-testid="insight-preview" className="text-sm border border-black/15 px-4 py-2.5 rounded-full hover:bg-black/5">Pré-visualizar ↗</a>)}
          <button onClick={save} disabled={saving} data-testid="insight-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange disabled:opacity-50"><Save className="w-3 h-3 inline mr-1" /> Salvar</button>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-3">
          <div><label className="text-xs text-neutral-500">Título</label><input value={item.title} onChange={(e) => upd("title", e.target.value)} data-testid="insight-title" className="w-full border border-black/15 px-3 py-2 rounded text-lg font-semibold" /></div>
          <div><label className="text-xs text-neutral-500">Slug</label><input value={item.slug} onChange={(e) => upd("slug", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>
          <div><label className="text-xs text-neutral-500">Resumo</label><textarea value={item.excerpt} onChange={(e) => upd("excerpt", e.target.value)} rows={2} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>
          <div className="grid grid-cols-4 gap-2">
            <div><label className="text-xs text-neutral-500">Assunto</label><input value={item.topic} onChange={(e) => upd("topic", e.target.value)} data-testid="insight-topic" className="w-full border border-black/15 px-2 py-2 rounded text-sm" /></div>
            <div><label className="text-xs text-neutral-500">Autor</label><input value={item.author} onChange={(e) => upd("author", e.target.value)} className="w-full border border-black/15 px-2 py-2 rounded text-sm" /></div>
            <div><label className="text-xs text-neutral-500">Cargo</label><input value={item.role} onChange={(e) => upd("role", e.target.value)} className="w-full border border-black/15 px-2 py-2 rounded text-sm" /></div>
            <div><label className="text-xs text-neutral-500">Min leitura</label><input type="number" value={item.readMin} onChange={(e) => upd("readMin", e.target.value)} className="w-full border border-black/15 px-2 py-2 rounded text-sm" /></div>
          </div>
          <div><label className="text-xs text-neutral-500 mb-2 block">Corpo do artigo</label>
            <Editor
              tinymceScriptSrc="https://cdn.jsdelivr.net/npm/tinymce@7/tinymce.min.js"
              onInit={(_e, editor) => (editorRef.current = editor)}
              initialValue={item.body || "<p></p>"}
              init={{
                height: 500, menubar: false,
                plugins: "lists link image autolink code preview quickbars wordcount",
                toolbar: "undo redo | h2 h3 | bold italic | bullist numlist | blockquote link image | removeformat code",
                content_style: "body{font-family:Poppins,sans-serif;font-size:15px;line-height:1.7;}",
                license_key: "gpl",
              }}
            />
          </div>
          <div><label className="text-xs text-neutral-500">Principais aprendizados (1 por linha)</label><textarea value={Array.isArray(item.keyLearnings) ? item.keyLearnings.join("\n") : item.keyLearnings} onChange={(e) => upd("keyLearnings", e.target.value)} rows={4} className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" /></div>

          {/* Content blocks — bloco de mídia rica: texto formatado, YouTube, imagem, lista colorida, citação, áudio, PDF */}
          <div className="border border-black/10 rounded-md p-4 mt-4">
            <div className="text-sm uppercase tracking-widest font-semibold text-neutral-700 mb-1">Blocos ricos do artigo</div>
            <p className="text-xs text-neutral-500 mb-3">Adicione blocos ao redor do corpo do artigo: texto formatado, vídeos do YouTube, imagens (com legenda), listas coloridas, citações, áudios narrados e <b>PDFs com thumb+título</b>. Cada bloco pode ser posicionado <b>antes</b> ou <b>depois</b> do texto do post pelo toggle no topo do bloco.</p>
            <InsightBlocksEditor blocks={item.content_blocks || []} onChange={(v) => upd("content_blocks", v)} testidPrefix="insight-content-blocks" />
          </div>

          <TranslationTabs
            value={item.translations || {}}
            onChange={(v) => upd("translations", v)}
            sourceFields={{ title: item.title, excerpt: item.excerpt, body: item.body }}
            fields={INSIGHT_TRANSLATABLE_FIELDS}
            context="insight"
            testidPrefix="insight-tt"
          />

          {/* ============ SEO (Advanced) ============ */}
          <div className="border border-accent-orange/30 bg-orange-50/40 rounded-md p-4 mt-6" data-testid="insight-seo-section">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] uppercase tracking-widest bg-accent-orange text-white px-2 py-0.5 rounded">SEO Avançado</span>
              <span className="text-xs text-neutral-500">Sobrescreve os padrões globais em /admin/seo</span>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-neutral-500 flex justify-between"><span>Meta title (título nos buscadores — 50-60 caracteres)</span><span className="font-mono">{(item.meta_title || "").length}/60</span></label>
                <input value={item.meta_title || ""} onChange={(e) => upd("meta_title", e.target.value)} data-testid="insight-meta-title" placeholder="Ex: Como narrativa aumenta o VGV de lançamentos imobiliários" className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-neutral-500 flex justify-between"><span>Meta description (155-160 caracteres recomendados)</span><span className="font-mono">{(item.meta_description || "").length}/160</span></label>
                <textarea value={item.meta_description || ""} onChange={(e) => upd("meta_description", e.target.value)} rows={2} data-testid="insight-meta-desc" placeholder="Resumo persuasivo que aparece nos resultados de busca." className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-neutral-500">Palavra-chave principal (focus keyword)</label>
                  <input value={item.main_keyword || ""} onChange={(e) => upd("main_keyword", e.target.value)} data-testid="insight-main-kw" placeholder="ex: narrativa imobiliária" className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
                </div>
                <div>
                  <label className="text-xs text-neutral-500">Palavras-chave secundárias (vírgulas)</label>
                  <input value={Array.isArray(item.secondary_keywords) ? item.secondary_keywords.join(", ") : (item.secondary_keywords || "")} onChange={(e) => upd("secondary_keywords", e.target.value)} data-testid="insight-secondary-kw" placeholder="branding, lançamento, VGV" className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-neutral-500">Slug (URL do artigo)</label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-neutral-400 font-mono">/insights/</span>
                  <input value={item.slug || ""} onChange={(e) => upd("slug", e.target.value)} data-testid="insight-seo-slug" placeholder="auto-gerado a partir do título se vazio" className="flex-1 border border-black/15 px-3 py-2 rounded text-sm font-mono" />
                </div>
                <div className="text-[10px] text-neutral-400 mt-1">Apenas letras minúsculas, números e hífens. Ex: <code>narrativa-imobiliaria-vgv</code></div>
              </div>
              <div>
                <label className="text-xs text-neutral-500">Imagem OG específica (opcional — usa a Capa se vazio)</label>
                <input value={item.og_image || ""} onChange={(e) => upd("og_image", e.target.value)} data-testid="insight-og-image" placeholder="URL 1200×630" className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
              </div>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!item.noindex} onChange={(e) => upd("noindex", e.target.checked)} data-testid="insight-noindex" /> Marcar este artigo como <code>noindex</code> (não aparece no Google)</label>
            </div>
          </div>
        </div>
        <aside>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Capa</div>
          {item.cover && <img src={resolveSrc(item.cover)} alt="" className="w-full aspect-video object-cover rounded mb-2" />}
          <DropZone multiple={false} onUploaded={(f) => upd("cover", f.url)} />
          <input value={item.cover || ""} onChange={(e) => upd("cover", e.target.value)} placeholder="ou URL" className="w-full text-xs border border-black/15 px-2 py-1.5 rounded mt-2" />
          <div className="mt-4"><label className="text-xs text-neutral-500">Ano</label><input type="number" value={item.year} onChange={(e) => upd("year", e.target.value)} className="w-full border border-black/15 px-2 py-2 rounded text-sm" /></div>
        </aside>
      </div>
    </div>
  );
}
